import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BookOpen,
  FileOutput,
  FolderOpen,
  MessageSquare,
  RotateCcw,
  Sparkles,
  Upload,
  AlertTriangle,
} from 'lucide-react'
import { useChatSessions } from '@/features/chat/use-chat'
import { useVaultFiles, useIngestionJobs } from '@/features/vault/use-vault'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EmptyState } from '@/components/empty-state'
import { SkeletonList } from '@/components/skeleton-list'
import { ErrorState } from '@/components/error-state'
import { StatusBadge } from '@/components/status-badge'
import { Skeleton } from '@/components/ui/skeleton'

const INGESTION_STATUS = {
  pending: { label: 'Wartend', variant: 'warning' },
  processing: { label: 'In Arbeit', variant: 'warning' },
  completed: { label: 'Fertig', variant: 'success' },
  completed_with_warnings: { label: 'Fertig mit Hinweisen', variant: 'warning' },
  failed: { label: 'Fehler', variant: 'danger' },
  cancelled: { label: 'Abgebrochen', variant: 'default' },
} as const

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function RecentList({
  title,
  icon: Icon,
  children,
  isLoading,
  error,
}: {
  title: string
  icon: typeof MessageSquare
  children: React.ReactNode
  isLoading: boolean
  /** M5-A: Fehlerzustand ersetzt den (irreführenden) Empty-State bei Query-Fehler. */
  error?: { message: string; onRetry: () => void }
}) {
  return (
    <Card className="shadow-none">
      <CardHeader className="p-4 pb-3">
        <CardTitle className="flex items-center gap-2 text-[16px] font-[650] text-foreground">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {isLoading ? (
          <SkeletonList rows={3} />
        ) : error ? (
          <div className="flex flex-col items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-3">
            <p className="flex items-center gap-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {error.message}
            </p>
            <Button variant="outline" size="sm" onClick={error.onRetry}>
              <RotateCcw className="mr-2 h-3.5 w-3.5" />
              Erneut versuchen
            </Button>
          </div>
        ) : (
          <div className="divide-y">{children}</div>
        )}
      </CardContent>
    </Card>
  )
}

function RecentItem({
  title,
  meta,
  onClick,
  icon: Icon,
}: {
  title: string
  meta?: string
  onClick: () => void
  icon: typeof MessageSquare
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 min-h-11 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{title}</p>
        {meta && <p className="truncate text-xs text-muted-foreground">{meta}</p>}
      </div>
    </button>
  )
}

// F04a: kompakte sachliche Bestandsangabe statt grosser Metrikkarte.
// Reiner Zähler — kein Gesundheits-, Prüf- oder Freigabe-Schluss.
function StatLine({
  label,
  value,
  testId,
}: {
  label: string
  value: React.ReactNode
  testId?: string
}) {
  return (
    <div
      data-testid={testId}
      className="flex items-baseline justify-between gap-3 rounded-md border px-3 py-2.5"
    >
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold text-foreground">{value}</span>
    </div>
  )
}

export function StartPage() {
  const navigate = useNavigate()

  const sessionsQuery = useChatSessions()
  const vaultQuery = useVaultFiles()
  const ingestionQuery = useIngestionJobs()
  const sessions = sessionsQuery.data
  const vault = vaultQuery.data
  const ingestion = ingestionQuery.data
  const sessionsLoading = sessionsQuery.isLoading
  const vaultLoading = vaultQuery.isLoading
  const ingestionLoading = ingestionQuery.isLoading

  const files = vault?.entries ?? []
  const jobs = ingestion?.jobs ?? []
  const outputFiles = files.filter((f) => f.path.startsWith('_Outputs/'))

  const sourceCount = files.filter((f) => f.type === 'file' && !f.path.startsWith('_Outputs/')).length
  const failedJobs = jobs.filter((j) => j.status === 'failed').length
  const processingJobs = jobs.filter((j) => j.status === 'pending' || j.status === 'processing').length

  const recentSessions = useMemo(
    () =>
      (sessions?.sessions ?? [])
        .filter((s) => s.message_count > 0)
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
        .slice(0, 5),
    [sessions],
  )

  const recentSources = useMemo(
    () =>
      files
        .filter((f) => f.type === 'file' && !f.path.startsWith('_Outputs/'))
        .sort((a, b) => new Date(b.updated_at ?? 0).getTime() - new Date(a.updated_at ?? 0).getTime())
        .slice(0, 5),
    [files],
  )

  const recentOutputs = useMemo(
    () =>
      outputFiles
        .sort((a, b) => new Date(b.updated_at ?? 0).getTime() - new Date(a.updated_at ?? 0).getTime())
        .slice(0, 5),
    [outputFiles],
  )

  const isLoading = sessionsLoading || vaultLoading || ingestionLoading

  return (
    // F04a: kompakte Heute-Seite — max 1180px, Desktop 48px Rand, mobil 16px.
    <div className="mx-auto w-full max-w-[1180px] px-4 py-6 min-[761px]:px-12 min-[761px]:py-8">
      {/* F04a: Titel «Heute» — kein Hero, keine Begrüssung. Workspace-Kontext
          steht sichtbar in der Shell (Topbar). */}
      <div className="flex flex-col gap-4 pb-6 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-[26px] font-[650] leading-tight tracking-tight text-foreground">Heute</h1>
        <div className="flex flex-wrap items-center gap-2">
          {/* Hauptaktion: Standard-Button (blau) */}
          <Button onClick={() => navigate('/fragen')}>
            <MessageSquare className="mr-2 h-4 w-4" />
            Frage stellen
          </Button>
          {/* Sekundäre ruhige kompakte Aktionen zu vorhandenen Zielen */}
          <Button variant="outline" size="sm" onClick={() => navigate('/quellen')}>
            <Upload className="mr-2 h-3.5 w-3.5" />
            Quelle hinzufügen
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/wissen')}>
            <BookOpen className="mr-2 h-3.5 w-3.5" />
            Wissen prüfen
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/automatisierungen')}>
            <Sparkles className="mr-2 h-3.5 w-3.5" />
            Output erzeugen
          </Button>
        </div>
      </div>

      {/* Wissensstatus: kompakte sachliche Bestands-/Verarbeitungsangaben */}
      <h2 className="mb-3 text-[16px] font-[650] tracking-tight text-foreground">Wissensstatus</h2>
      <div className="mb-8 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {isLoading ? (
          <>
            <Skeleton className="h-11 w-full" />
            <Skeleton className="h-11 w-full" />
            <Skeleton className="h-11 w-full" />
            <Skeleton className="h-11 w-full" />
          </>
        ) : vaultQuery.isError || ingestionQuery.isError ? (
          <ErrorState
            className="sm:col-span-2 lg:col-span-4"
            title="Wissensstatus konnte nicht geladen werden"
            description="Bitte versuche es erneut."
            isRetrying={vaultQuery.isFetching || ingestionQuery.isFetching}
            onRetry={() => {
              if (vaultQuery.isError) void vaultQuery.refetch()
              if (ingestionQuery.isError) void ingestionQuery.refetch()
            }}
          />
        ) : (
          <>
            <StatLine testId="stat-sources" label="Quellen im Bestand" value={sourceCount} />
            <StatLine
              testId="stat-processing"
              label="Verarbeitung"
              value={`${processingJobs} in Arbeit`}
            />
            <StatLine testId="stat-errors" label="Fehler" value={failedJobs} />
            <StatLine testId="stat-outputs" label="Outputs im Bestand" value={outputFiles.length} />
          </>
        )}
      </div>

      {/* Letzte Aktivität: ruhige kompakte Zeilen */}
      <h2 className="mb-3 text-[16px] font-[650] tracking-tight text-foreground">Letzte Aktivität</h2>
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
        <RecentList
          title="Fragen"
          icon={MessageSquare}
          isLoading={sessionsLoading}
          error={
            sessionsQuery.isError
              ? {
                  message: 'Fragen konnten nicht geladen werden.',
                  onRetry: () => void sessionsQuery.refetch(),
                }
              : undefined
          }
        >
          {recentSessions.length === 0 ? (
            <EmptyState
              icon={MessageSquare}
              title="Noch keine Fragen"
              description="Stelle deine erste Frage an dein Wissen."
              action={{ label: 'Frage stellen', onClick: () => navigate('/fragen') }}
            />
          ) : (
            recentSessions.map((s) => (
              <RecentItem
                key={s.id}
                icon={MessageSquare}
                title={s.title || 'Neue Konversation'}
                meta={`${s.message_count} Nachrichten`}
                onClick={() => navigate(`/fragen?session=${s.id}`)}
              />
            ))
          )}
        </RecentList>

        <RecentList
          title="Quellen"
          icon={FolderOpen}
          isLoading={vaultLoading}
          error={
            vaultQuery.isError
              ? {
                  message: 'Quellen konnten nicht geladen werden.',
                  onRetry: () => void vaultQuery.refetch(),
                }
              : undefined
          }
        >
          {recentSources.length === 0 ? (
            <EmptyState
              icon={FolderOpen}
              title="Noch keine Quellen"
              description="Lade Dokumente hoch oder verbinde einen Connector."
              action={{ label: 'Quelle hinzufügen', onClick: () => navigate('/quellen') }}
            />
          ) : (
            recentSources.map((f) => (
              <RecentItem
                key={f.path}
                icon={FolderOpen}
                title={f.name}
                meta={formatDate(f.updated_at ?? new Date().toISOString())}
                onClick={() => navigate(`/quellen?path=${encodeURIComponent(f.path)}`)}
              />
            ))
          )}
        </RecentList>

        <RecentList
          title="Outputs"
          icon={FileOutput}
          isLoading={vaultLoading}
          error={
            vaultQuery.isError
              ? {
                  message: 'Outputs konnten nicht geladen werden.',
                  onRetry: () => void vaultQuery.refetch(),
                }
              : undefined
          }
        >
          {recentOutputs.length === 0 ? (
            <EmptyState
              icon={FileOutput}
              title="Noch keine Outputs"
              description="Erzeuge deinen ersten Output aus deinem Wissen."
              action={{ label: 'Output erzeugen', onClick: () => navigate('/automatisierungen') }}
            />
          ) : (
            recentOutputs.map((f) => (
              <RecentItem
                key={f.path}
                icon={FileOutput}
                title={f.name}
                meta={formatDate(f.updated_at ?? new Date().toISOString())}
                onClick={() => navigate(`/outputs?path=${encodeURIComponent(f.path)}`)}
              />
            ))
          )}
        </RecentList>
      </div>

      {/* Verarbeitung: kompakte Statuszeilen */}
      {!ingestionLoading && jobs.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-3 text-[16px] font-[650] tracking-tight text-foreground">Verarbeitung</h2>
          <Card>
            <CardContent className="p-0">
              <div className="divide-y">
                {jobs.slice(0, 5).map((job) => (
                  <div key={job.id} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium" title={job.vault_file_name}>
                        {job.vault_file_name}
                      </p>
                      <p className="text-xs text-muted-foreground">{job.model ?? 'Automatisch'}</p>
                    </div>
                    <StatusBadge variant={INGESTION_STATUS[job.status].variant}>
                      {INGESTION_STATUS[job.status].label}
                    </StatusBadge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
